'use client';

import { useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { Turnstile } from '@marsidev/react-turnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, Loader2, Mail, XCircle } from 'lucide-react';
import { useFormValidation } from '@/hooks/useFormValidation';
import { unsubscribeFormSchema } from '@/lib/validation';
import { ErrorMessage } from '@/components/ui/error-message';

function UnsubscribeContent() {
    const searchParams = useSearchParams();
    const token = searchParams.get('token');

    const [email, setEmail] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    // 'confirm': arrived from a signed link; nothing is sent until the visitor clicks, because mail
    // scanners that execute JavaScript would otherwise unsubscribe people just by opening the link.
    // 'unsubscribed': a signed link was confirmed. 'link-sent': an address was entered — the server only
    // ever e-mails it a link, and answers the same whether or not the address is on the list.
    // 'invalid-link': the token was rejected, so the form is shown with an explanation.
    const [status, setStatus] = useState<'confirm' | 'idle' | 'unsubscribed' | 'link-sent' | 'invalid-link' | 'error'>(
        token ? 'confirm' : 'idle'
    );
    const [message, setMessage] = useState('');
    const { errors, validate, validateField } = useFormValidation(unsubscribeFormSchema);

    const handleUnsubscribe = async (unsubscribeToken?: string) => {
        // Validate form if not using token
        if (!unsubscribeToken) {
            const formData = { email, captchaToken };
            if (!validate(formData)) {
                return;
            }
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await fetch('/api/unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: unsubscribeToken,
                    email: unsubscribeToken ? undefined : email,
                    captchaToken: unsubscribeToken ? undefined : captchaToken,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setStatus(unsubscribeToken ? 'unsubscribed' : 'link-sent');
                setMessage(data.message || '');
            } else {
                setStatus(unsubscribeToken ? 'invalid-link' : 'error');
                setMessage(data.error || 'Failed to unsubscribe');
            }
        } catch (_error) {
            setStatus('error');
            setMessage('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                {status === 'confirm' && token ? (
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe from eIDAS Pro</h1>
                        <p className="text-gray-600 mb-6">
                            Confirm below and we will stop sending e-mails to this address.
                        </p>
                        <Button
                            onClick={() => handleUnsubscribe(token)}
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                            disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Unsubscribing...
                                </>
                            ) : (
                                'Confirm Unsubscribe'
                            )}
                        </Button>
                        <p className="text-xs text-gray-500 mt-4 text-center">
                            Changed your mind?{' '}
                            <a href="/" className="text-indigo-600 hover:underline">
                                Return to homepage
                            </a>
                        </p>
                    </div>
                ) : status === 'unsubscribed' ? (
                    <div className="text-center">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribed Successfully</h1>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <p className="text-sm text-gray-500">
                            You will no longer receive emails from eIDAS Pro. We&apos;re sorry to see you go!
                        </p>
                        <Button
                            onClick={() => (window.location.href = '/')}
                            className="mt-6 bg-indigo-600 hover:bg-indigo-700">
                            Return to Homepage
                        </Button>
                    </div>
                ) : status === 'link-sent' ? (
                    <div className="text-center">
                        <Mail className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Inbox</h1>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <p className="text-sm text-gray-500">
                            Open the link in that e-mail to confirm. Nothing changes until you do.
                        </p>
                        <Button
                            onClick={() => (window.location.href = '/')}
                            className="mt-6 bg-indigo-600 hover:bg-indigo-700">
                            Return to Homepage
                        </Button>
                    </div>
                ) : status === 'error' ? (
                    <div className="text-center">
                        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe Failed</h1>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <Button
                            onClick={() => {
                                setStatus('idle');
                                setMessage('');
                            }}
                            variant="outline"
                            className="mt-4">
                            Try Again
                        </Button>
                    </div>
                ) : (
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe from eIDAS Pro</h1>
                        {status === 'invalid-link' ? (
                            <div
                                role="alert"
                                className="mb-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <XCircle className="w-5 h-5 shrink-0" />
                                <span>{message}</span>
                            </div>
                        ) : null}
                        <p className="text-gray-600 mb-6">
                            We&apos;re sorry to see you go. Enter your email below and we&apos;ll send you a link to
                            unsubscribe from our waiting list.
                        </p>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleUnsubscribe();
                            }}
                            className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => validateField('email', email)}
                                    placeholder="your.email@example.com"
                                    required
                                    className={`w-full ${errors['email'] ? 'border-red-500' : ''}`}
                                    disabled={loading}
                                />
                                <ErrorMessage message={errors['email']} />
                            </div>

                            <Turnstile
                                siteKey={process.env['NEXT_PUBLIC_TURNSTILE_SITE_KEY'] ?? '1x00000000000000000000AA'}
                                onSuccess={(token) => setCaptchaToken(token)}
                                onExpire={() => setCaptchaToken(null)}
                                onError={() => setCaptchaToken(null)}
                                options={{ appearance: 'interaction-only' }}
                            />

                            <Button
                                type="submit"
                                className="w-full bg-indigo-600 hover:bg-indigo-700"
                                disabled={loading || !captchaToken}>
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    'Send Unsubscribe Link'
                                )}
                            </Button>
                        </form>

                        <p className="text-xs text-gray-500 mt-4 text-center">
                            Changed your mind?{' '}
                            <a href="/" className="text-indigo-600 hover:underline">
                                Return to homepage
                            </a>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function UnsubscribePage() {
    return (
        <Suspense
            fallback={
                <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                        <Loader2 className="w-16 h-16 text-indigo-600 mx-auto mb-4 animate-spin" />
                        <p className="text-gray-600">Loading...</p>
                    </div>
                </div>
            }>
            <UnsubscribeContent />
        </Suspense>
    );
}
